import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Message } from '@/interface/chat';

export interface ChatState {
  input: string;
  chatHistory: Message[];
  loading: boolean;
  error: string | null;
  chatId: string | null;
  hasUnprocessedMessage: boolean;
  currentPage: number;
  totalPages: number;
  voiceMode: boolean;
  isProcessingMessage: boolean;
  unprocessedMessage: Message | null;
  showTypingIndicator: boolean;
  isStreaming: boolean;
  streamingText: string;
  conversationStreaming: {
    [conversationId: string]: {
      isStreaming: boolean;
      streamingText: string;
      showTypingIndicator: boolean;
      isProcessingMessage: boolean;
      unprocessedMessage: Message | null;
    };
  };
}

const initialState: ChatState = {
  input: '',
  chatHistory: [],
  loading: false,
  error: null,
  chatId: null,
  hasUnprocessedMessage: false,
  currentPage: 1,
  totalPages: 1,
  voiceMode: false,
  isProcessingMessage: false,
  unprocessedMessage: null,
  showTypingIndicator: false,
  isStreaming: false,
  streamingText: '',
  conversationStreaming: {},
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<Message>) => {
      state.chatHistory.push(action.payload);
      if (action.payload.isNew) {
        state.hasUnprocessedMessage = true;
      }
    },
    removeMessage: (state, action: PayloadAction<string>) => {
      state.chatHistory = state.chatHistory.filter(
        (msg) => msg.id !== action.payload,
      );
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setIsProcessingMessage: (state, action: PayloadAction<boolean>) => {
      state.isProcessingMessage = action.payload;
      const id = state.chatId;
      if (id) {
        const existing = state.conversationStreaming[id] ?? {
          isStreaming: false,
          streamingText: '',
          showTypingIndicator: false,
          isProcessingMessage: false,
          unprocessedMessage: null,
        };
        state.conversationStreaming[id] = {
          ...existing,
          isProcessingMessage: action.payload,
        };
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setChatHistory: (state, action: PayloadAction<Message[]>) => {
      const payload = action.payload ?? [];
      const byKey = new Map<string, Message>();
      const existingMessages = new Map(
        state.chatHistory.map((msg) => [msg.id, msg]),
      );

      for (const msg of payload) {
        const idKey = msg.messageId ?? msg.id ?? msg.uId;
        const key = `${msg.conversationId ?? ''}::${idKey ?? JSON.stringify(msg)}`;
        if (msg.id && existingMessages.has(msg.id)) {
          const existing = existingMessages.get(msg.id)!;
          if (existing.response && !msg.response) {
            msg.response = existing.response;
            if (existing.sql_query && !msg.sql_query)
              msg.sql_query = existing.sql_query;
            if (existing.table_used && !msg.table_used)
              msg.table_used = existing.table_used;
          }
        }

        if (byKey.has(key)) byKey.delete(key);
        byKey.set(key, msg);
      }

      state.chatHistory = Array.from(byKey.values());
      state.hasUnprocessedMessage = false;
    },
    updateMessage: (state, action: PayloadAction<Message>) => {
      const index = state.chatHistory.findIndex(
        (msg) => msg.id === action.payload.id,
      );
      if (index !== -1) {
        state.chatHistory[index] = action.payload;
        // state.hasUnprocessedMessage = false;
        // state.unprocessedMessage = null;
      } else {
        if (action.payload.conversationId === state.chatId) {
          // state.hasUnprocessedMessage = false;
          state.chatHistory.push(action.payload);
          // state.unprocessedMessage = null;
        }
      }
    },
    updateMessageId: (state, action: PayloadAction<Message>) => {
      const index = state.chatHistory.findIndex(
        (msg) => msg.id === action.payload.id,
      );
      if (index !== -1) {
        state.chatHistory[index].messageId = action.payload.messageId;
        if (action.payload.graphData !== undefined) {
          state.chatHistory[index].graphData = action.payload.graphData;
        }
      }
    },
    setUnprocessedMessage: (state, action: PayloadAction<Message | null>) => {
      state.unprocessedMessage = action.payload;
      const conversationId = action.payload?.conversationId;
      if (conversationId) {
        const existing = state.conversationStreaming[conversationId] ?? {
          isStreaming: false,
          streamingText: '',
          showTypingIndicator: false,
          isProcessingMessage: false,
          unprocessedMessage: null,
        };
        state.conversationStreaming[conversationId] = {
          ...existing,
          unprocessedMessage: action.payload,
        };
      }
    },
    setChatId: (state, action: PayloadAction<string | null>) => {
      state.chatId = action.payload;
      const id = action.payload;
      if (id && state.conversationStreaming[id]) {
        const cs = state.conversationStreaming[id];
        state.isStreaming = cs.isStreaming;
        state.streamingText = cs.streamingText;
        state.showTypingIndicator = cs.showTypingIndicator;
        state.unprocessedMessage = cs.unprocessedMessage;
        state.isProcessingMessage = cs.isProcessingMessage;
      } else {
        state.isStreaming = false;
        state.streamingText = '';
        state.showTypingIndicator = false;
        state.unprocessedMessage = null;
        state.isProcessingMessage = false;
      }
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTotalPages: (state, action: PayloadAction<number>) => {
      state.totalPages = action.payload;
    },
    setVoiceMode: (state, action: PayloadAction<boolean>) => {
      state.voiceMode = action.payload;
    },
    setHasUnprocessedMessage: (state, action: PayloadAction<boolean>) => {
      state.hasUnprocessedMessage = action.payload;
    },
    clearChatHistory: (state) => {
      state.chatHistory = [];
      state.currentPage = 1;
      state.totalPages = 1;
      state.hasUnprocessedMessage = false;
      state.chatId = null;
      // state.unprocessedMessage = null;
    },
    setShowTypingIndicator: (state, action: PayloadAction<boolean>) => {
      state.showTypingIndicator = action.payload;
      const id = state.chatId;
      if (id) {
        const existing = state.conversationStreaming[id] ?? {
          isStreaming: false,
          streamingText: '',
          showTypingIndicator: false,
          isProcessingMessage: false,
          unprocessedMessage: null,
        };
        state.conversationStreaming[id] = {
          ...existing,
          showTypingIndicator: action.payload,
        };
      }
    },
    setIsStreaming: (state, action: PayloadAction<boolean>) => {
      state.isStreaming = action.payload;
      const id = state.chatId;
      if (id) {
        const existing = state.conversationStreaming[id] ?? {
          isStreaming: false,
          streamingText: '',
          showTypingIndicator: false,
          isProcessingMessage: false,
          unprocessedMessage: null,
        };
        state.conversationStreaming[id] = {
          ...existing,
          isStreaming: action.payload,
        };
      }
    },
    setStreamingText: (state, action: PayloadAction<string>) => {
      state.streamingText = action.payload;
      const id = state.chatId;
      if (id) {
        const existing = state.conversationStreaming[id] ?? {
          isStreaming: false,
          streamingText: '',
          showTypingIndicator: false,
          isProcessingMessage: false,
          unprocessedMessage: null,
        };
        state.conversationStreaming[id] = {
          ...existing,
          streamingText: action.payload,
        };
      }
    },
    setConversationStreamingState: (
      state,
      action: PayloadAction<{
        conversationId: string;
        isStreaming?: boolean;
        streamingText?: string;
        showTypingIndicator?: boolean;
        isProcessingMessage?: boolean;
        unprocessedMessage?: Message | null;
      }>,
    ) => {
      const {
        conversationId,
        isStreaming,
        streamingText,
        showTypingIndicator,
        isProcessingMessage,
        unprocessedMessage,
      } = action.payload;

      const existing = state.conversationStreaming[conversationId] ?? {
        isStreaming: false,
        streamingText: '',
        showTypingIndicator: false,
        isProcessingMessage: false,
        unprocessedMessage: null,
      };

      const updated = {
        ...existing,
        ...(isStreaming !== undefined ? { isStreaming } : {}),
        ...(streamingText !== undefined ? { streamingText } : {}),
        ...(showTypingIndicator !== undefined ? { showTypingIndicator } : {}),
        ...(isProcessingMessage !== undefined ? { isProcessingMessage } : {}),
        ...(unprocessedMessage !== undefined ? { unprocessedMessage } : {}),
      };

      state.conversationStreaming[conversationId] = updated;

      if (state.chatId === conversationId) {
        if (isStreaming !== undefined) {
          state.isStreaming = isStreaming;
        }
        if (streamingText !== undefined) {
          state.streamingText = streamingText;
        }
        if (showTypingIndicator !== undefined) {
          state.showTypingIndicator = showTypingIndicator;
        }
        if (isProcessingMessage !== undefined) {
          state.isProcessingMessage = isProcessingMessage;
        }
        if (unprocessedMessage !== undefined) {
          state.unprocessedMessage = unprocessedMessage;
        }
      }
    },
  },
});

export const {
  addMessage,
  removeMessage,
  setLoading,
  setError,
  setChatHistory,
  updateMessage,
  setChatId,
  updateMessageId,
  setCurrentPage,
  setTotalPages,
  clearChatHistory,
  setHasUnprocessedMessage,
  setIsProcessingMessage,
  setUnprocessedMessage,
  setVoiceMode,
  setShowTypingIndicator,
  setIsStreaming,
  setStreamingText,
  setConversationStreamingState,
} = chatSlice.actions;

export default chatSlice.reducer;
